import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, {} = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.twofa_required) {
      // Do NOT set user yet — caller must complete 2FA
      return data;
    }
    if (data?.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data);
    return data;
  };

  const complete2FA = async ({ temp_token, method, code }) => {
    const { data } = await api.post("/auth/2fa/verify", { temp_token, method, code });
    if (data?.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    // Best-effort server-side logout — clear local state regardless of outcome.
    try { await api.post("/auth/logout"); }
    catch (e) { console.warn("Logout API failed; clearing local session anyway", e); }
    localStorage.removeItem("access_token");
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, complete2FA }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
